import React, { useState, useEffect } from 'react';
import './Calculator.css'; // Make sure to create this CSS file

import { Heading } from '../../../components';

import { FaHome, FaCamera, FaVideo, FaClock, FaHdd, FaLock } from 'react-icons/fa';
import { FaAngleDown } from 'react-icons/fa6';



const Calculator = () => {

  // Form state
  const [formData, setFormData] = useState({
    propertyType: '',
    numCameras: '',
    cameraType: '',
    recordingDuration: '',
    storageOptions: '',
    accessControl: '',
    installation: false,
    monitoring: false,
    maintenance: false
  });

  const [totalCost, setTotalCost] = useState(0);

  // Cost factors
  const costs = {
    propertyType: {
      "single-story": 100,
      "multi-story": 200,
      "commercial": 300,
      "industrial": 500,
      "apartment": 150,
    },
    cameraType: {
      "standard": 50,
      "hd": 100,
      "ptz": 150,
      "wireless": 120,
      "dome": 80,
    },
    recordingDuration: {
      "24/7": 200,
      "motion-detection": 100,
      "scheduled": 150,
    },
    storageOptions: {
      "cloud": 100,
      "local": 50,
    },
    accessControl: {
      "yes": 200,
      "no": 0,
    },
    additionalServices: {
      "installation": 100,
      "monitoring": 150,
      "maintenance": 50,
    },
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Calculate total cost whenever form data changes
  useEffect(() => {
    calculateTotalCost();
  }, [formData]);

  const calculateTotalCost = () => {
    let cost = 0;

    // Property type cost
    if (formData.propertyType && costs.propertyType[formData.propertyType]) {
      cost += costs.propertyType[formData.propertyType];
    }

    // Camera type and number of cameras cost
    if (formData.cameraType && costs.cameraType[formData.cameraType]) {
      const numCameras = parseInt(formData.numCameras) || 0;
      cost += costs.cameraType[formData.cameraType] * numCameras;
    }

    // Recording duration cost
    if (formData.recordingDuration && costs.recordingDuration[formData.recordingDuration]) {
      cost += costs.recordingDuration[formData.recordingDuration];
    }

    // Storage options cost
    if (formData.storageOptions && costs.storageOptions[formData.storageOptions]) {
      cost += costs.storageOptions[formData.storageOptions];
    }

    // Access control system cost
    if (formData.accessControl && costs.accessControl[formData.accessControl]) {
      cost += costs.accessControl[formData.accessControl];
    }

    // Additional services costs
    if (formData.installation) cost += costs.additionalServices.installation;
    if (formData.monitoring) cost += costs.additionalServices.monitoring;
    if (formData.maintenance) cost += costs.additionalServices.maintenance;

    setTotalCost(cost);
  };

  return (
    <section className="custom-form cost-calculator linear-bg">
      <div className="box-container">
        <div className="content">

          <form className="calculator-form dark-form">

            {/* Form Heading */}
            <Heading title={'Security System Calculator'} subTitle={'Estimate Your Security System'} sideHeading linear />

            <div className="input-box">

              {/* Property Type Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="property-type" className="icon-label">
                    <FaHome />
                  </label>
                  <select
                    className="select-box"
                    id="property-type"
                    name="propertyType"
                    value={formData.propertyType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Property Type</option>
                    <option value="single-story">Single-Story House</option>
                    <option value="multi-story">Multi-Story House</option>
                    <option value="commercial">Commercial Building</option>
                    <option value="industrial">Industrial Property</option>
                    <option value="apartment">Apartment</option>
                  </select>
                  <div className="icon-container">
                    <FaAngleDown />
                  </div>
                </div>
              </div>

              {/* Number of Security Cameras Input Field */}
              <div className="input-field">
                <label htmlFor="num-cameras" className="icon-label">
                  <FaCamera />
                </label>
                <input
                  type="number"
                  name="numCameras"
                  className="box"
                  id="num-cameras"
                  min="1"
                  value={formData.numCameras}
                  onChange={handleInputChange}
                  placeholder="Number of Cameras"
                  required
                />
              </div>

            </div>

            <div className="input-box">

              {/* Camera Type Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="camera-type" className="icon-label">
                    <FaVideo />
                  </label>
                  <select
                    className="select-box"
                    id="camera-type"
                    name="cameraType"
                    value={formData.cameraType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Camera Type</option>
                    <option value="standard">Standard Camera</option>
                    <option value="hd">HD Camera</option>
                    <option value="ptz">PTZ Camera</option>
                    <option value="wireless">Wireless Camera</option>
                    <option value="dome">Dome Camera</option>
                  </select>
                  <div className="icon-container">
                    <FaAngleDown />
                  </div>
                </div>
              </div>

              {/* Recording Duration Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="recording-duration" className="icon-label">
                    <FaClock />
                  </label>
                  <select
                    className="select-box"
                    id="recording-duration"
                    name="recordingDuration"
                    value={formData.recordingDuration}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Recording Duration</option>
                    <option value="24/7">24/7 Recording</option>
                    <option value="motion-detection">Motion Detection Only</option>
                    <option value="scheduled">Scheduled Recording</option>
                  </select>
                  <div className="icon-container">
                    <FaAngleDown />
                  </div>
                </div>
              </div>

            </div>

            <div className="input-box">

              {/* Storage Options Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="storage-options" className="icon-label">
                    <FaHdd />
                  </label>
                  <select
                    className="select-box"
                    id="storage-options"
                    name="storageOptions"
                    value={formData.storageOptions}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Storage Options</option>
                    <option value="cloud">Cloud Storage</option>
                    <option value="local">Local Storage (DVR/Hard Drive)</option>
                  </select>
                  <div className="icon-container">
                    <FaAngleDown />
                  </div>
                </div>
              </div>

              {/* Access Control System Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="access-control" className="icon-label">
                    <FaLock />
                  </label>
                  <select
                    className="select-box"
                    id="access-control"
                    name="accessControl"
                    value={formData.accessControl}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Access Control System</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  <div className="icon-container">
                    <FaAngleDown />
                  </div>
                </div>
              </div>

            </div>

            {/* Additional Services Checkboxes */}
            <div className="extra-services">

              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="installation-checkbox"
                  name="installation"
                  checked={formData.installation}
                  onChange={handleInputChange}
                />
                <label htmlFor="installation-checkbox">Installation</label>
                <div className="checkbox-indicator"></div>
              </div>

              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="monitoring-checkbox"
                  name="monitoring"
                  checked={formData.monitoring}
                  onChange={handleInputChange}
                />
                <label htmlFor="monitoring-checkbox">Professional Monitoring</label>
                <div className="checkbox-indicator"></div>
              </div>

              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="maintenance-checkbox"
                  name="maintenance"
                  checked={formData.maintenance}
                  onChange={handleInputChange}
                />
                <label htmlFor="maintenance-checkbox">Maintenance</label>
                <div className="checkbox-indicator"></div>
              </div>

            </div>

            {/* Total Cost Display */}
            <div className="cost-total">
              <h5>Total Estimate</h5>
              <span id="total-cost">${totalCost}</span>
            </div>

          </form>

        </div>
      </div>
    </section>
  );
};

export default Calculator;